import * as React from "react";
import { useMemo } from "react";
import { BarcodeFormat, EncodeHintType, QRCodeWriter } from "@zxing/library";

interface QrCodeProps {
  value: string;
  size?: number;
  className?: string;
}

interface QrModule {
  x: number;
  y: number;
}

const buildModules = (
  value: string,
): { modules: QrModule[]; dimension: number } => {
  if (!value) {
    return { modules: [], dimension: 0 };
  }
  const hints = new Map<EncodeHintType, string | number>([
    [EncodeHintType.ERROR_CORRECTION, "M"],
  ]);
  const matrix = new QRCodeWriter().encode(
    value,
    BarcodeFormat.QR_CODE,
    0,
    0,
    hints,
  );
  const dimension = matrix.getWidth();
  const modules: QrModule[] = [];
  for (let y = 0; y < matrix.getHeight(); y++) {
    for (let x = 0; x < dimension; x++) {
      if (matrix.get(x, y)) {
        modules.push({ x, y });
      }
    }
  }
  return { modules, dimension };
};

export const QrCode: React.FC<QrCodeProps> = ({
  value,
  size = 240,
  className,
}) => {
  const { modules, dimension } = useMemo(() => buildModules(value), [value]);

  if (dimension === 0) {
    return null;
  }

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${dimension} ${dimension}`}
      role="img"
      aria-label="QR-Code"
      shapeRendering="crispEdges"
    >
      <rect
        width={dimension}
        height={dimension}
        className="fill-qr-bg"
      />
      {modules.map((module) => (
        <rect
          key={`${module.x}-${module.y}`}
          x={module.x}
          y={module.y}
          width={1}
          height={1}
          className="fill-qr-fg"
        />
      ))}
    </svg>
  );
};
